import {
  addTimeToDateString,
  calculateBaseDateRange,
} from "src/utils/formatTime";
import { format } from "date-fns";
import axios from "../util/axios";
import Token from "../util/Token";

//DASHBOARD URL
const URL = "/cms/v1/team";

async function getStatistics(teamId, timeInterval) {
  Token.setAuthToken();
  return await axios.get(`${URL}/${teamId}/dashboard_stats`, {
    params: { ...timeInterval },
  });
}
async function getIndividualStatistics(teamId, statsUrl, timeInterval, field) {
  try {
    Token.setAuthToken();
    const updatedTimeInterval = calculateBaseDateRange(timeInterval);
    const { status, data } = await axios.get(`${URL}/${teamId}/${statsUrl}`, {
      params: { ...updatedTimeInterval },
    });
    if (status === 200 && data.success) {
      return { ...data.data, field };
    }
  } catch (error) {
    // Error handling
  }
}

// New function specifically for transactions graph data that passes start, end, baseStart, and baseEnd
async function getTransactions(teamId, timeInterval) {
  try {
    Token.setAuthToken();
    
    // Calculate baseStart and baseEnd for comparison (previous period)
    const baseTimeInterval = calculateBaseDateRange(timeInterval);
    
    // Format dates to YYYY-MM-DD format for the API
    const formatDateForAPI = (dateString) => {
      const date = new Date(dateString);
      // Use local date parts to ensure we send the IST date, not the UTC date.
      // This matches the new backend CONVERT_TZ logic.
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    const params = { 
      start: formatDateForAPI(timeInterval.start),
      end: formatDateForAPI(timeInterval.end),
      baseStart: formatDateForAPI(baseTimeInterval.baseStart),
      baseEnd: formatDateForAPI(baseTimeInterval.baseEnd)
    };
    
    
    const { status, data } = await axios.get(`${URL}/${teamId}/transactions/graph`, {
      params
    });
    
    if (status === 200 && data.success) {
      return data.data; // Return the array directly instead of spreading it
    }
    
    return null;
  } catch (error) {
    return null;
  }
}
// Pre-aggregated trend buckets (energy/sessions/revenue) for the dashboard charts.
// Tiny payload — one row per day/week/month — instead of raw transactions.
async function getTransactionTrends(teamId, timeInterval, granularity) {
  try {
    Token.setAuthToken();
    const toApiDate = (dateString) => {
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };
    const params = {
      granularity,
      start: toApiDate(timeInterval.start),
      end: toApiDate(timeInterval.end),
    };
    const { status, data } = await axios.get(
      `${URL}/${teamId}/transactions/trends`,
      { params }
    );
    if (status === 200 && data.success) {
      return data.data;
    }
    return [];
  } catch (error) {
    return [];
  }
}

// Network KPI summary for the dashboard header tiles. An optional timeInterval
// ({ start, end } ISO) scopes the time-based KPIs (sessions/energy/revenue/
// failed) to that range; omitted => today.
async function getNetworkSummary(teamId, timeInterval) {
  try {
    Token.setAuthToken();
    const params = {};
    if (timeInterval?.start && timeInterval?.end) {
      const toApiDate = (dateString) => {
        const date = new Date(dateString);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      };
      params.start = toApiDate(timeInterval.start);
      params.end = toApiDate(timeInterval.end);
    }
    const { status, data } = await axios.get(`${URL}/${teamId}/network-summary`, {
      params,
    });
    if (status === 200 && data.success) return data.data;
    return null;
  } catch (error) {
    return null;
  }
}

// Peak-hour heatmap (7x24 session-count grid).
async function getPeakHourHeatmap(teamId) {
  try {
    Token.setAuthToken();
    const { status, data } = await axios.get(
      `${URL}/${teamId}/transactions/peak-hours`
    );
    if (status === 200 && data.success) return data.data;
    return null;
  } catch (error) {
    return null;
  }
}

// Per-company Dashboard V2 flag (Company → Settings tab). Decoupled from login:
// the read is resilient server-side and defaults to false on any failure here.
const COMPANY_URL = "/cms/v2/company";

// Read the CALLER's own company flag (keyed to the JWT server-side). This is
// what the dashboard uses to choose V1 vs V2: it's authenticated-only (not
// gated behind the COMPANY feature permission), so every company user can read
// it, and it needs no companyId from the client.
async function getDashboardConfig() {
  try {
    Token.setAuthToken();
    const { status, data } = await axios.get("/cms/v1/dashboard-config");
    if (status === 200 && data.success) {
      return { dashboardV2Enabled: data.data?.dashboardV2Enabled === true };
    }
    return { dashboardV2Enabled: false };
  } catch (error) {
    return { dashboardV2Enabled: false };
  }
}

// Set the CALLER's own company flag (super-admin System Settings self-toggle).
// GP-only server-side; targets the JWT's own company, so no companyId needed.
async function updateOwnDashboardConfig(dashboardV2Enabled) {
  Token.setAuthToken();
  const { data } = await axios.put("/cms/v1/dashboard-config", {
    dashboardV2Enabled: dashboardV2Enabled === true,
  });
  return { dashboardV2Enabled: data?.data?.dashboardV2Enabled === true };
}

// Read a SPECIFIC company's flag (super-admin Company → Settings tab).
async function getCompanyDashboardConfig(companyId) {
  try {
    Token.setAuthToken();
    const { status, data } = await axios.get(
      `${COMPANY_URL}/${companyId}/dashboard-config`
    );
    if (status === 200 && data.success) {
      return { dashboardV2Enabled: data.data?.dashboardV2Enabled === true };
    }
    return { dashboardV2Enabled: false };
  } catch (error) {
    return { dashboardV2Enabled: false };
  }
}

async function updateCompanyDashboardConfig(companyId, dashboardV2Enabled) {
  Token.setAuthToken();
  const { data } = await axios.put(
    `${COMPANY_URL}/${companyId}/dashboard-config`,
    { dashboardV2Enabled: dashboardV2Enabled === true }
  );
  return { dashboardV2Enabled: data?.data?.dashboardV2Enabled === true };
}

const dashboardService = {
  getStatistics,
  getIndividualStatistics,
  getTransactions,
  getTransactionTrends,
  getNetworkSummary,
  getPeakHourHeatmap,
  getDashboardConfig,
  updateOwnDashboardConfig,
  getCompanyDashboardConfig,
  updateCompanyDashboardConfig,
};
export default dashboardService;
